import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import ToolPageTemplate from '../templates/ToolPageTemplate'
import Input from '../atoms/Input'
import RangeSlider from '../molecules/RangeSlider'
import SelectDropdown from '../molecules/SelectDropdown'
import Button from '../atoms/Button'
import {
  setText, setVoice, setRate, setPitch, setVolume, setAvailableVoices,
  setPlaying, setPaused, setError, clearTtsState,
  addPronunciationPair, removePronunciationPair,
} from '../store/slices/ttsSlice'
import './TextToSpeechPage.css'

const CHAR_LIMIT = 5000
const CHAR_WARN_THRESHOLD = 0.85
const AVG_WPM = 150
const LAST_TEXT_STORAGE_KEY = 'everykits_tts_last_text'
const MAX_UPLOAD_BYTES = 2 * 1024 * 1024

const detectDominantScript = (str) => {
  if (!str || !str.trim()) return null
  const arabicCount = (str.match(/[\u0600-\u06FF\u0750-\u077F]/g) || []).length
  const devanagariCount = (str.match(/[\u0900-\u097F]/g) || []).length
  const latinCount = (str.match(/[A-Za-z]/g) || []).length
  const max = Math.max(arabicCount, devanagariCount, latinCount)
  if (max === 0) return null
  if (max === arabicCount) return 'ur'
  if (max === devanagariCount) return 'hi'
  return 'en'
}

const TextToSpeechPage = ({ tools = [] }) => {
  const dispatch = useDispatch()
  const {
    text, voice, rate, pitch, volume, isPlaying, isPaused,
    availableVoices, error, pronunciationPairs,
  } = useSelector(state => state.tts)
  const synthRef = useRef(window.speechSynthesis)
  const fileInputRef = useRef(null)
  const readingDisplayRef = useRef(null)
  const activeWordRef = useRef(null)

  const [activeWordIndex, setActiveWordIndex] = useState(-1)
  const [showPronDict, setShowPronDict] = useState(false)
  const [pronFrom, setPronFrom] = useState('')
  const [pronTo, setPronTo] = useState('')
  const [pronDuplicateWarning, setPronDuplicateWarning] = useState(false)

  const [draftAvailable, setDraftAvailable] = useState(null)
  const [draftDismissed, setDraftDismissed] = useState(false)

  const [isDraggingFile, setIsDraggingFile] = useState(false)
  const [uploadStatus, setUploadStatus] = useState(null)
  const uploadStatusTimeoutRef = useRef(null)

  const [lastUpdated, setLastUpdated] = useState(null)
  useEffect(() => {
    setLastUpdated(
      new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    )
  }, [])

  useEffect(() => {
    const loadVoices = () => {
      const voices = synthRef.current.getVoices()
      const plainVoices = voices.map(v => ({
        voiceURI: v.voiceURI,
        name: v.name,
        lang: v.lang,
        localService: v.localService,
        default: v.default,
      }))
      dispatch(setAvailableVoices(plainVoices))
    }

    loadVoices()

    if (synthRef.current.onvoiceschanged !== undefined) {
      synthRef.current.onvoiceschanged = loadVoices
    }

    return () => {
      if (synthRef.current.speaking) {
        synthRef.current.cancel()
      }
    }
  }, [dispatch])

  useEffect(() => {
    const saved = localStorage.getItem(LAST_TEXT_STORAGE_KEY)
    if (saved && saved.trim()) {
      setDraftAvailable(saved)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (text) {
        localStorage.setItem(LAST_TEXT_STORAGE_KEY, text)
      } else {
        localStorage.removeItem(LAST_TEXT_STORAGE_KEY)
      }
    }, 500)
    return () => clearTimeout(timeout)
  }, [text])

  useEffect(() => {
    setActiveWordIndex(-1)
  }, [text])

  useEffect(() => {
    if (activeWordRef.current && readingDisplayRef.current) {
      activeWordRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [activeWordIndex])

  useEffect(() => {
    return () => clearTimeout(uploadStatusTimeoutRef.current)
  }, [])

  const wordTokens = useMemo(() => {
    const tokens = []
    const regex = /\S+/g
    let match
    while ((match = regex.exec(text)) !== null) {
      tokens.push({ word: match[0], start: match.index, end: match.index + match[0].length })
    }
    return tokens
  }, [text])

  const hindiVoiceCount = useMemo(
    () => availableVoices.filter(v => v.lang?.toLowerCase().startsWith('hi')).length,
    [availableVoices]
  )
  const urduVoiceCount = useMemo(
    () => availableVoices.filter(v => v.lang?.toLowerCase().startsWith('ur')).length,
    [availableVoices]
  )

  const estimatedSeconds = useMemo(() => {
    const words = text.trim() ? text.trim().split(/\s+/).length : 0
    const wpm = AVG_WPM * (rate || 1)
    return wpm > 0 ? Math.round((words / wpm) * 60) : 0
  }, [text, rate])

  const formatDuration = (totalSeconds) => {
    if (!totalSeconds) return '0 sec'
    const mins = Math.floor(totalSeconds / 60)
    const secs = totalSeconds % 60
    if (mins === 0) return `${secs} sec`
    return `${mins} min ${secs} sec`
  }

  const charPercent = Math.min(100, (text.length / CHAR_LIMIT) * 100)
  const charBarState = text.length > CHAR_LIMIT
    ? 'over'
    : charPercent >= CHAR_WARN_THRESHOLD * 100
      ? 'warn'
      : 'ok'

  const getSpokenText = useCallback(() => {
    let spoken = text
    pronunciationPairs.forEach(({ from, to }) => {
      if (!from) return
      const escaped = from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      const re = new RegExp(`(?<![\\p{L}\\p{N}])${escaped}(?![\\p{L}\\p{N}])`, 'giu')
      spoken = spoken.replace(re, to)
    })
    return spoken
  }, [text, pronunciationPairs])

  const hasActiveReplacements = pronunciationPairs.length > 0
  const detectedScript = useMemo(() => detectDominantScript(text), [text])

  const currentVoiceLang = useMemo(() => {
    const v = availableVoices.find(v => v.name === voice)
    return v?.lang?.toLowerCase() || ''
  }, [availableVoices, voice])

  const findVoiceForScript = useCallback((script) => {
    if (script === 'ur') {
      return (
        availableVoices.find(v => v.lang?.toLowerCase().startsWith('ur')) ||
        availableVoices.find(v => v.lang?.toLowerCase().startsWith('ar')) ||
        null
      )
    }
    if (script === 'hi') {
      return availableVoices.find(v => v.lang?.toLowerCase().startsWith('hi')) || null
    }
    return null
  }, [availableVoices])

  const scriptMismatch = useMemo(() => {
    if (!detectedScript || detectedScript === 'en') return null
    const matchesCurrent = detectedScript === 'ur'
      ? (currentVoiceLang.startsWith('ur') || currentVoiceLang.startsWith('ar'))
      : currentVoiceLang.startsWith('hi')
    if (matchesCurrent) return null
    return { script: detectedScript, suggestedVoice: findVoiceForScript(detectedScript) }
  }, [detectedScript, currentVoiceLang, findVoiceForScript])

  const textDirection = detectedScript === 'ur' ? 'rtl' : 'auto'

  const handleTextChange = (e) => {
    dispatch(setText(e.target.value))
  }

  const handleVoiceChange = (e) => dispatch(setVoice(e.target.value))
  const handleRateChange = (e) => dispatch(setRate(parseFloat(e.target.value)))
  const handlePitchChange = (e) => dispatch(setPitch(parseFloat(e.target.value)))
  const handleVolumeChange = (e) => dispatch(setVolume(parseFloat(e.target.value)))

  const handleSwitchSuggestedVoice = () => {
    if (scriptMismatch?.suggestedVoice) {
      dispatch(setVoice(scriptMismatch.suggestedVoice.name))
    }
  }

  const handlePlay = () => {
    if (synthRef.current.speaking && !isPaused) return

    if (isPaused) {
      synthRef.current.resume()
      dispatch(setPaused(false))
      return
    }

    if (!text.trim()) {
      dispatch(setError('Please enter some text to speak'))
      return
    }

    const spokenText = getSpokenText()
    const utterance = new SpeechSynthesisUtterance(spokenText)

    const allVoices = synthRef.current.getVoices()
    const selectedVoice = allVoices.find(v => v.name === voice)
    if (selectedVoice) utterance.voice = selectedVoice

    utterance.rate = rate
    utterance.pitch = pitch
    utterance.volume = volume

    utterance.onstart = () => {
      dispatch(setPlaying(true))
      dispatch(setPaused(false))
      dispatch(setError(null))
    }

    utterance.onend = () => {
      dispatch(setPlaying(false))
      dispatch(setPaused(false))
      setActiveWordIndex(-1)
    }

    utterance.onerror = (event) => {
      if (event.error !== 'interrupted' && event.error !== 'canceled') {
        dispatch(setError(`Speech error: ${event.error}`))
      }
      dispatch(setPlaying(false))
      dispatch(setPaused(false))
      setActiveWordIndex(-1)
    }

    if (!hasActiveReplacements) {
      utterance.onboundary = (event) => {
        if (event.name === 'word' || event.name === undefined) {
          const idx = wordTokens.findIndex(t => event.charIndex >= t.start && event.charIndex < t.end)
          if (idx !== -1) setActiveWordIndex(idx)
        }
      }
    }

    synthRef.current.speak(utterance)
  }

  const handlePause = () => {
    if (synthRef.current.speaking && !isPaused) {
      synthRef.current.pause()
      dispatch(setPaused(true))
    }
  }

  const handleStop = () => {
    synthRef.current.cancel()
    dispatch(setPlaying(false))
    dispatch(setPaused(false))
    setActiveWordIndex(-1)
  }

  const handleClear = () => {
    handleStop()
    dispatch(clearTtsState())
  }

  const handleRestoreDraft = () => {
    dispatch(setText(draftAvailable))
    setDraftDismissed(true)
  }
  const handleDiscardDraft = () => {
    localStorage.removeItem(LAST_TEXT_STORAGE_KEY)
    setDraftDismissed(true)
  }

  const showUploadStatus = (type, message) => {
    clearTimeout(uploadStatusTimeoutRef.current)
    setUploadStatus({ type, message })
    uploadStatusTimeoutRef.current = setTimeout(() => setUploadStatus(null), 4000)
  }

  const processUploadedFile = (file) => {
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.txt')) {
      showUploadStatus('error', 'Only .txt files are supported right now.')
      return
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      showUploadStatus('error', 'File is too large (max 2MB).')
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      const uploaded = (event.target.result || '').trim()
      if (!uploaded) {
        showUploadStatus('error', 'That file appears to be empty.')
        return
      }
      const combined = text.trim() ? `${text.trim()}\n\n${uploaded}` : uploaded
      dispatch(setText(combined))
      showUploadStatus('success', `Added ${uploaded.length.toLocaleString()} characters from ${file.name}`)
    }
    reader.onerror = () => showUploadStatus('error', 'Could not read that file.')
    reader.readAsText(file)
  }

  const handleFileButtonClick = () => fileInputRef.current?.click()

  const handleFileInputChange = (e) => {
    processUploadedFile(e.target.files[0])
    e.target.value = ''
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDraggingFile(true)
  }
  const handleDragLeave = () => setIsDraggingFile(false)
  const handleDrop = (e) => {
    e.preventDefault()
    setIsDraggingFile(false)
    processUploadedFile(e.dataTransfer.files?.[0])
  }

  const handleAddPronunciation = () => {
    const from = pronFrom.trim()
    const to = pronTo.trim()
    if (!from || !to) return

    const isDuplicate = pronunciationPairs.some(
      (p) => p.from.toLowerCase() === from.toLowerCase()
    )
    if (isDuplicate) {
      setPronDuplicateWarning(true)
      setTimeout(() => setPronDuplicateWarning(false), 2500)
      return
    }

    dispatch(addPronunciationPair({ from, to }))
    setPronFrom('')
    setPronTo('')
  }

  const handlePronKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddPronunciation()
    }
  }

  const handleEditPronunciation = (pair) => {
    setPronFrom(pair.from)
    setPronTo(pair.to)
    dispatch(removePronunciationPair(pair.id))
  }

  const voiceOptions = availableVoices.map(v => ({
    value: v.name,
    label: `${v.name} (${v.lang})`
  }))

  const showDraftBanner = draftAvailable && !draftDismissed && !text

  return (
    <ToolPageTemplate path="/text-to-speech/" tools={tools}>
      <div>
        <div className="text-to-speech">
          {showDraftBanner && (
            <div className="tts-draft-banner" role="status">
              <span>You have an unfinished draft from your last visit.</span>
              <div className="tts-draft-actions">
                <button type="button" className="draft-btn draft-btn-primary" onClick={handleRestoreDraft}>
                  Restore
                </button>
                <button type="button" className="draft-btn" onClick={handleDiscardDraft}>
                  Discard
                </button>
              </div>
            </div>
          )}

          {scriptMismatch && (
            <div className="tts-script-banner" role="status">
              <span>
                This looks like {scriptMismatch.script === 'ur' ? 'Urdu/Arabic' : 'Hindi'} script, but your selected voice doesn't speak it — the audio may sound incorrect.
              </span>
              {scriptMismatch.suggestedVoice ? (
                <button type="button" className="draft-btn draft-btn-primary" onClick={handleSwitchSuggestedVoice}>
                  Switch to {scriptMismatch.suggestedVoice.name}
                </button>
              ) : (
                <span className="script-banner-note">
                  No {scriptMismatch.script === 'ur' ? 'Urdu or Arabic' : 'Hindi'} voice found on this device.
                </span>
              )}
            </div>
          )}

          <div
            className={`tts-input-section${isDraggingFile ? ' dragging' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Input
              as="textarea"
              label="Enter text to speak"
              value={text}
              onChange={handleTextChange}
              placeholder="Type here, paste text, or drop a .txt file anywhere in this box..."
              rows={6}
              dir={textDirection}
              style={{ resize: 'vertical' }}
            />

            {isDraggingFile && (
              <div className="drop-overlay" aria-hidden="true">Drop your .txt file to add it here</div>
            )}

            {isPlaying && wordTokens.length > 0 && !hasActiveReplacements && (
              <div className="tts-reading-display" ref={readingDisplayRef} dir={textDirection} aria-live="off">
                {wordTokens.map((token, i) => (
                  <span
                    key={i}
                    ref={i === activeWordIndex ? activeWordRef : null}
                    className={`reading-word${i === activeWordIndex ? ' active' : ''}`}
                  >
                    {token.word}{' '}
                  </span>
                ))}
              </div>
            )}

            <div className="tts-char-meter">
              <div className="char-bar-track">
                <div className={`char-bar-fill char-bar-${charBarState}`} style={{ width: `${charPercent}%` }} />
              </div>
              <div className="tts-info">
                <span className="char-count">
                  <span className={charBarState === 'over' ? 'char-count-warning' : ''}>
                    {text.length.toLocaleString()}
                  </span>
                  {' '}/ {CHAR_LIMIT.toLocaleString()} characters · ~{formatDuration(estimatedSeconds)} to read
                </span>
                <div className="tts-info-actions">
                  <input
                    type="file"
                    accept=".txt,text/plain"
                    ref={fileInputRef}
                    onChange={handleFileInputChange}
                    style={{ display: 'none' }}
                  />
                  <button type="button" className="upload-btn" onClick={handleFileButtonClick}>
                    Upload .txt
                  </button>
                  <button className="clear-btn" onClick={handleClear}>
                    Clear
                  </button>
                </div>
              </div>
            </div>

            {uploadStatus && (
              <div className={`upload-status upload-status-${uploadStatus.type}`} role="status">
                {uploadStatus.message}
              </div>
            )}

            <div className="tts-pronunciation">
              <button
                type="button"
                className="pron-toggle"
                onClick={() => setShowPronDict(!showPronDict)}
                aria-expanded={showPronDict}
              >
                {showPronDict ? 'Hide' : 'Show'} Pronunciation Dictionary
                {pronunciationPairs.length > 0 && ` (${pronunciationPairs.length})`}
              </button>
              {showPronDict && (
                <div className="pron-panel">
                  <p className="pron-hint">
                    Fix how a specific word is spoken — like a brand name or abbreviation — without editing your text.
                  </p>
                  <div className="pron-form">
                    <input
                      type="text"
                      placeholder="Word (e.g. EveryKits)"
                      value={pronFrom}
                      onChange={(e) => setPronFrom(e.target.value)}
                      onKeyDown={handlePronKeyDown}
                    />
                    <input
                      type="text"
                      placeholder="Pronounce as (e.g. Every Kits)"
                      value={pronTo}
                      onChange={(e) => setPronTo(e.target.value)}
                      onKeyDown={handlePronKeyDown}
                    />
                    <Button onClick={handleAddPronunciation} disabled={!pronFrom.trim() || !pronTo.trim()}>
                      Add
                    </Button>
                  </div>
                  {pronDuplicateWarning && (
                    <p className="pron-duplicate-warning">That word already has a pronunciation set — edit it below instead.</p>
                  )}
                  {pronunciationPairs.length > 0 && (
                    <ul className="pron-list">
                      {pronunciationPairs.map((p) => (
                        <li key={p.id}>
                          <button
                            type="button"
                            className="pron-list-label"
                            onClick={() => handleEditPronunciation(p)}
                            title="Click to edit"
                          >
                            <strong>{p.from}</strong> → {p.to}
                          </button>
                          <button
                            type="button"
                            className="pron-remove-btn"
                            onClick={() => dispatch(removePronunciationPair(p.id))}
                            aria-label={`Remove pronunciation for ${p.from}`}
                          >
                            ✕
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="tts-controls">
            <div className="control-grid">
              <div className="voice-meta">
                <SelectDropdown
                  label="Voice"
                  value={voice}
                  onChange={handleVoiceChange}
                  options={voiceOptions}
                />
                <div className="voice-status-row">
                  <span className="voice-status-pill">
                    {availableVoices.length} voice{availableVoices.length === 1 ? '' : 's'}
                  </span>
                  <span className={`voice-status-pill ${hindiVoiceCount > 0 ? 'pill-active' : 'pill-muted'}`}>
                    <span className="pill-dot" /> Hindi: {hindiVoiceCount}
                  </span>
                  <span className={`voice-status-pill ${urduVoiceCount > 0 ? 'pill-active' : 'pill-muted'}`}>
                    <span className="pill-dot" /> Urdu: {urduVoiceCount}
                  </span>
                </div>
              </div>

              <RangeSlider label="Rate" value={rate} onChange={handleRateChange} min={0.1} max={2} step={0.1} unit="x" />
              <RangeSlider label="Pitch" value={pitch} onChange={handlePitchChange} min={0.1} max={2} step={0.1} unit="x" />
              <RangeSlider label="Volume" value={volume} onChange={handleVolumeChange} min={0} max={1} step={0.1} unit="" />
            </div>

            <div className="tts-buttons">
              <Button onClick={handlePlay} disabled={!text.trim()}>
                {isPaused ? 'Resume' : 'Play'}
              </Button>
              <Button variant="secondary" onClick={handlePause} disabled={!isPlaying || isPaused}>
                Pause
              </Button>
              <Button variant="danger" onClick={handleStop} disabled={!isPlaying}>
                Stop
              </Button>
            </div>
          </div>

          {error && <div className="tts-error">{error}</div>}

          {isPlaying && (
            <div className="tts-status">
              <div className="speaking-indicator">
                <span className="speaking-dot"></span>
                <span className="speaking-dot"></span>
                <span className="speaking-dot"></span>
              </div>
              <span>{isPaused ? 'Paused' : 'Speaking...'}</span>
            </div>
          )}
        </div>

        <section className="tool-guide">
          <div className="guide-section">
            <h2>What Is Text to Speech?</h2>
            <p>Text to speech (TTS) converts written text into spoken audio using a synthesized voice. Instead of recording a human voice, a TTS engine analyzes the text and generates speech in real time — which is why the same sentence can be read instantly in dozens of different voices and languages.</p>
            <p>This tool uses your browser's own built-in speech engine (the Web Speech API) rather than a cloud AI service. That's a deliberate choice: it means the conversion happens on your device, your text never leaves your browser, and there's no server round-trip slowing things down. The trade-off is that voice selection depends on what's installed on your operating system rather than a proprietary voice library — we'll walk through exactly what that means below.</p>
          </div>

          <div className="guide-section">
            <h2>Follow Along With Live Word Highlighting</h2>
            <p>As the voice reads, the word currently being spoken highlights on screen in real time. We built this specifically for two situations we kept running into ourselves: proofreading a draft where hearing and seeing the exact word catches mistakes silent reading misses, and reading along with content for anyone who processes text better when it's paired with audio, including many people with dyslexia.</p>
            <p>One honest limitation: if you're using the Pronunciation Dictionary below to change how a word sounds, highlighting turns off for that playback. The spoken audio and the visible text are no longer word-for-word identical at that point, so we'd rather disable the highlight than show you a position that's slightly off.</p>
          </div>

          {/* NEW: Browser compatibility */}
          <div className="guide-section">
            <h2>Browser Compatibility</h2>
            <p>Because this tool relies on the Web Speech API rather than a server, compatibility depends on your browser and operating system:</p>
            <div className="use-cases">
              <div className="use-case">
                <h3>Chrome (Desktop, Android)</h3>
                <p><strong>Full support.</strong> Wide voice selection, including Hindi and Urdu on most devices. The most reliable option for this tool.</p>
              </div>
              <div className="use-case">
                <h3>Safari (macOS, iOS)</h3>
                <p><strong>Full support.</strong> Voice selection includes Apple's system voices. Quality is generally excellent; Hindi support is common, Urdu support varies by iOS version and installed language packs.</p>
              </div>
              <div className="use-case">
                <h3>Edge (Windows, macOS)</h3>
                <p><strong>Full support.</strong> Uses Microsoft's neural voices on Windows, which are among the best-sounding free voices available. Excellent for long-form reading.</p>
              </div>
              <div className="use-case">
                <h3>Firefox</h3>
                <p><strong>Supported</strong>, but voice selection depends entirely on the operating system — Firefox does not ship its own voices. On Linux, additional packages may be needed.</p>
              </div>
            </div>
          </div>

          {/* NEW: Voice comparison table */}
          <div className="guide-section">
            <h2>Which Voice and Settings for Which Use Case</h2>
            <p>There is no single "best" voice — the right choice depends on what you're doing. These are the combinations that work best for the most common use cases:</p>
            <div className="use-cases">
              <div className="use-case">
                <h3>Proofreading a draft</h3>
                <p><strong>Voice:</strong> Any English · <strong>Rate:</strong> 0.9–1.0x · <strong>Pitch:</strong> default. Speaking slightly slower than normal catches skipped words and awkward phrasing that silent reading glides over.</p>
              </div>
              <div className="use-case">
                <h3>Language learning</h3>
                <p><strong>Voice:</strong> Native speaker of the target language · <strong>Rate:</strong> 0.7–0.8x · <strong>Pitch:</strong> default. Slower rate helps with unfamiliar phonemes, and native voice quality matters far more than on other tasks.</p>
              </div>
              <div className="use-case">
                <h3>Accessibility reading</h3>
                <p><strong>Voice:</strong> Clear, well-modulated voice · <strong>Rate:</strong> user preference (usually 1.0x) · <strong>Volume:</strong> high. Voice clarity matters more than novelty here.</p>
              </div>
              <div className="use-case">
                <h3>Content skimming</h3>
                <p><strong>Voice:</strong> Any · <strong>Rate:</strong> 1.5–2.0x. At faster rates, most voices start to slur — a natural-sounding voice at 1.5x works better than a robotic voice at 2x.</p>
              </div>
              <div className="use-case">
                <h3>Reading to children</h3>
                <p><strong>Voice:</strong> Warm-sounding voice · <strong>Rate:</strong> 0.8–0.9x · <strong>Pitch:</strong> slightly higher than default. Slower and slightly higher pitch is easier for young listeners to follow.</p>
              </div>
              <div className="use-case">
                <h3>Long-form listening</h3>
                <p><strong>Voice:</strong> Neural voice if available (Edge on Windows) · <strong>Rate:</strong> 1.1–1.3x. Neural voices are dramatically more comfortable for extended listening than older concatenative voices.</p>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Fix How Any Word Is Pronounced</h2>
            <p>Browser voices are generally reliable, but they stumble on brand names, acronyms, and less common words — "EveryKits" itself is a good example most engines misread on the first try. The Pronunciation Dictionary lets you define exactly how a word should sound when it's spoken, without touching the text you see or copy. Add a pair once — the word as written, and how you want it pronounced — and it applies automatically every time you hit play. This works the same way for Hindi and Urdu text as it does for English.</p>
          </div>

          <div className="guide-section">
            <h2>Upload a File — or Combine Both</h2>
            <p>Prefer typing? Just use the box above. Have a longer draft saved elsewhere? Upload a .txt file and it adds to whatever you've already typed, so you can mix a quick note with pasted or uploaded content in the same read. Everything stays editable — upload doesn't lock the text box, and you can drag a file directly onto it too.</p>
          </div>

          <div className="guide-section">
            <h2>Hindi and Urdu Voice Support</h2>
            <p>This tool can read Hindi and Urdu text using whichever voices your device already has installed — we don't maintain a separate voice library, so there's nothing extra to download from us. In practice, availability depends on your operating system: Android devices (through Google's text-to-speech engine) almost always include both Hindi and Urdu voices by default. Windows and iOS support for Hindi is fairly common; Urdu support on those platforms is more inconsistent and depends on your device's installed language packs.</p>
            <p>The Voice dropdown above tells you exactly how many Hindi and Urdu voices are available on your specific device right now. If you type or paste Urdu or Hindi text while an incompatible voice is selected, the tool detects the mismatch and offers a one-click switch to a matching voice, if one exists on your device. If the count is zero, that's a device setting rather than a limitation of this tool — on Android, go to Settings → System → Languages → Text-to-speech output to check or download additional language voices for free.</p>
          </div>

          <div className="guide-section">
            <h2>Your Text Never Leaves Your Device</h2>
            <p>Most online text-to-speech tools send whatever you type to a cloud server to generate the audio — which means your text is transmitted, and in many cases logged, somewhere outside your control before you hear a single word. This tool works differently by design: because it runs on the Web Speech API built directly into your browser, the entire conversion — reading the text, selecting the voice, generating the audio — happens locally on your device. Nothing is uploaded, there's no account required to create, and no server ever sees what you typed.</p>
            <p>The one thing this tool does store is your last piece of text, saved only in your own browser's local storage, purely so you don't lose your place if you close the tab. That's optional convenience, not a requirement — discarding it takes one click.</p>
          </div>

          <div className="guide-section">
            <h2>How to Use This Text to Speech Tool</h2>
            <div className="guide-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Type, Paste, or Upload Your Text</h3>
                  <p>Use the box directly, or upload a .txt file — it adds to anything already typed rather than replacing it.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Choose a Voice</h3>
                  <p>Select from the available voices on your device, including Hindi or Urdu if your device supports them.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Adjust Rate, Pitch, and Volume</h3>
                  <p>Fine-tune the speaking speed, pitch, and volume — slower for careful proofreading, faster for a quick skim.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Play and Follow Along</h3>
                  <p>Click "Play" to start playback and watch each word highlight as it's spoken.</p>
                </div>
              </div>
            </div>
          </div>

          {/* EXPANDED FAQ — 9 → 12 */}
          <div className="guide-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-list">
              <div className="faq-item">
                <h3>Is this text to speech tool really free?</h3>
                <p>Yes — no account, no character limits enforced by us, and no watermark on the audio. It runs on your browser's built-in speech engine rather than a paid cloud API, so there's no cost for us to pass on.</p>
              </div>
              <div className="faq-item">
                <h3>Does it support Hindi or Urdu?</h3>
                <p>Yes, if your device has a Hindi or Urdu voice installed. Android devices generally do by default; Windows and iOS support varies. The Voice dropdown shows the exact count available on your device, and the tool will warn you and suggest a switch if your selected voice doesn't match the script you've typed.</p>
              </div>
              <div className="faq-item">
                <h3>Can I upload a file instead of typing?</h3>
                <p>Yes. Use the "Upload .txt" button, or drag a file directly onto the text box — it's added to whatever you've already typed and never sent to a server.</p>
              </div>
              <div className="faq-item">
                <h3>Is there a character limit?</h3>
                <p>There's no hard limit we enforce, but we recommend staying under roughly 5,000 characters per read for the most reliable playback, especially on mobile browsers.</p>
              </div>
              <div className="faq-item">
                <h3>Can I control how a specific word is pronounced?</h3>
                <p>Yes, using the Pronunciation Dictionary. It changes only what's spoken — your visible text stays exactly as you typed it. This works for Hindi and Urdu words as well as English.</p>
              </div>
              <div className="faq-item">
                <h3>Is my text sent to a server?</h3>
                <p>No. Speech synthesis happens entirely on your device via the Web Speech API. The only thing saved is your last text, stored locally in your own browser for convenience.</p>
              </div>
              <div className="faq-item">
                <h3>Can I download the audio as an MP3 or WAV file?</h3>
                <p>Not currently. This tool plays audio live using your device's speech engine rather than generating a downloadable file. We're evaluating this for a future update.</p>
              </div>
              <div className="faq-item">
                <h3>Why do the available voices look different on my phone versus my computer?</h3>
                <p>Voices come from your operating system, not from this website. Windows, macOS, iOS, and Android each ship a different built-in voice set, so what you see depends on your device.</p>
              </div>
              <div className="faq-item">
                <h3>Does it work on mobile browsers?</h3>
                <p>Yes, on most modern mobile browsers — the available voices, including Hindi and Urdu, depend on your phone's operating system.</p>
              </div>
              <div className="faq-item">
                <h3>What's the difference between text to speech and speech to text?</h3>
                <p>Text to speech converts written words into spoken audio (what this tool does). Speech to text does the opposite — it converts spoken audio into written text, typically used for transcription, dictation, and voice commands. They're complementary technologies, not alternatives.</p>
              </div>
              <div className="faq-item">
                <h3>Why does the voice stop in the middle of long text on some browsers?</h3>
                <p>Some browsers, particularly Chrome on Android and Safari on iOS, cut off long utterances after roughly 15 seconds. This is a browser-level limitation, not a bug in the tool. If you're reading text longer than 2,000 characters, splitting it into two or three shorter reads gives more reliable playback.</p>
              </div>
              <div className="faq-item">
                <h3>Can I use this tool offline?</h3>
                <p>Partly. Once the page has loaded, the Web Speech API itself works without an internet connection on most devices, because speech synthesis happens locally. However, the page needs to load once from the server first. After that, adding text, changing voices, and playing audio can all work offline in most browsers.</p>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Related Tools</h2>
            <div className="related-tools">
              <Link to="/word-counter/" className="related-tool">
                <div className="tool-icon">Word Counter</div>
                <div className="tool-description">Check length and readability before converting text to speech</div>
              </Link>
              <Link to="/json-formatter/" className="related-tool">
                <div className="tool-icon">JSON Formatter</div>
                <div className="tool-description">Format and validate JSON data</div>
              </Link>
              <Link to="/blog/how-text-to-speech-works/" className="related-tool">
                <div className="tool-icon">How Text to Speech Works</div>
                <div className="tool-description">A technical look at how TTS converts text into natural-sounding speech</div>
              </Link>
            </div>
          </div>
        </section>

        <div className="last-updated">
          <p suppressHydrationWarning>Last updated: {lastUpdated || 'Recently'}</p>
        </div>
      </div>
    </ToolPageTemplate>
  )
}

export default TextToSpeechPage